import { createClient } from '@supabase/supabase-js'

// Usar las variables de entorno
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Verificar que las variables estén configuradas
if (!supabaseUrl || !supabaseKey) {
  console.error('❌ Variables de entorno faltantes:')
  console.error('REACT_APP_SUPABASE_URL:', supabaseUrl ? '✅ Configurada' : '❌ Faltante')
  console.error('REACT_APP_SUPABASE_ANON_KEY:', supabaseKey ? '✅ Configurada' : '❌ Faltante')
  throw new Error('Faltan las variables de entorno de Supabase. Verifica tu archivo .env')
}

// Crear cliente de Supabase
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  }
})

/**
 * Función para verificar la conexión a Supabase con debugging mejorado
 * @returns {boolean} - true si la conexión es exitosa
 */
export const testConnection = async () => {
  try {
    console.log('🔍 Probando conexión a Supabase...')
    console.log('📍 URL:', supabaseUrl)
    console.log('🔑 Key (primeros 10 chars):', supabaseKey?.substring(0, 10) + '...')

    // Probar conexión a la tabla usuarios
    const { data, error: healthError } = await supabase
      .from('seace_news')
      .select('*')
      .limit(1)

    if (healthError) {
      console.error('❌ Error específico:', healthError)
      console.error('❌ Código de error:', healthError.code)
      console.error('❌ Mensaje:', healthError.message)
      console.error('❌ Detalles:', healthError.details)
      
      // Verificar si es un problema de tabla
      if (healthError.code === 'PGRST116' || healthError.message?.includes('relation') || healthError.message?.includes('does not exist')) {
        console.error('🚨 PROBLEMA: La tabla "usuarios" no existe o no tienes permisos')
        console.error('💡 Solución: Ve a Supabase → Table Editor → Verifica el nombre de la tabla')
      }
      
      // Verificar si es un problema de RLS
      if (healthError.code === '42501' || healthError.message?.includes('permission') || healthError.message?.includes('policy')) {
        console.error('🚨 PROBLEMA: Row Level Security (RLS) está bloqueando el acceso')
        console.error('💡 Solución: Ve a Supabase → Authentication → Policies → Deshabilita RLS o crea políticas')
      }
      
      return false
    }

    console.log('✅ Conexión a Supabase exitosa')
    console.log('📊 Datos obtenidos:', data?.length || 0, 'registros')
    return true
  } catch (error) {
    console.error('❌ Error probando conexión:', error)
    return false
  }
}

/**
 * Alias para testConnection (para compatibilidad)
 */
export const probarConexion = testConnection

/**
 * Función para obtener información de la base de datos
 * @returns {Object} - Información de la conexión
 */
export const getConnectionInfo = async () => {
  try {
    const { error } = await supabase
      .from('seace_news')
      .select('*')

    if (error) {
      return {
        connected: false,
        error: error.message,
        errorCode: error.code,
        timestamp: new Date().toISOString()
      }
    }

    return {
      connected: true,
      tableExists: true,
      timestamp: new Date().toISOString(),
      supabaseUrl: supabaseUrl
    }
  } catch (error) {
    return {
      connected: false,
      error: error.message,
      timestamp: new Date().toISOString()
    }
  }
}

/**
 * Función para crear la tabla si no existe (solo para debugging)
 */
export const verificarYCrearTabla = async () => {
  try {
    console.log('🔍 Verificando si la tabla usuarios existe...')
    
    // Intentar una consulta simple
    const { error } = await supabase
      .from('seace_news')
      .select('*')
      .limit(1)

    if (error) {
      console.error('❌ La tabla no existe o hay problemas de permisos')
      console.error('📋 Estructura SQL necesaria:')
      console.log(`
CREATE TABLE usuarios (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  correo text UNIQUE NOT NULL,
  nombre text,
  empresa text,
  plan text,
  celular text,
  ruc text,
  valor numeric,
  fecha_inicio date,
  mes_suscripcion integer,
  fecha_fin date,
  updated_at timestamp with time zone DEFAULT now()
);

-- Deshabilitar RLS temporalmente
ALTER TABLE usuarios DISABLE ROW LEVEL SECURITY;
      `)
      return false
    }

    console.log('✅ Tabla verificada correctamente')
    return true
  } catch (error) {
    console.error('❌ Error verificando tabla:', error)
    return false
  }
}

/**
 * Función para verificar si la tabla correos_validos existe y tiene la estructura correcta
 * @returns {Object} - Resultado de la verificación
 */
export const verificarEstructuraTabla = async () => {
  try {
    // Intentar hacer una consulta simple para verificar la existencia de la tabla
    const { error } = await supabase
      .from('seace_news')
      .select('*')
      .limit(1)

    if (error) {
      return {
        existe: false,
        error: error.message,
        errorCode: error.code,
        sugerencia: 'Verifica que la tabla "usuarios" exista en tu base de datos de Supabase'
      }
    }

    // Verificar campos requeridos
    const camposRequeridos = [
      'correo', 'nombre', 'empresa', 'plan', 'celular', 
      'ruc', 'valor', 'fecha_inicio', 'mes_suscripcion', 'fecha_fin'
    ]

    const verificacion = {
      existe: true,
      campos: camposRequeridos,
      mensaje: '✅ Tabla verificada correctamente'
    }

    return verificacion

  } catch (error) {
    return {
      existe: false,
      error: error.message,
      sugerencia: 'Error inesperado al verificar la tabla'
    }
  }
}

/**
 * Función de inicialización para verificar todo el setup
 * @returns {Object} - Estado de la inicialización
 */
export const initializeSupabase = async () => {
  console.log('🚀 Inicializando conexión con Supabase...')
  
  try {
    // 1. Verificar variables de entorno
    if (!supabaseUrl || !supabaseKey) {
      return {
        success: false,
        step: 'variables',
        message: 'Variables de entorno faltantes'
      }
    }

    // 2. Probar conexión
    const connectionOk = await testConnection()
    if (!connectionOk) {
      return {
        success: false,
        step: 'connection',
        message: 'No se pudo conectar a Supabase'
      }
    }

    // 3. Verificar tabla
    const tablaVerificacion = await verificarEstructuraTabla()
    if (!tablaVerificacion.existe) {
      return {
        success: false,
        step: 'table',
        message: 'Tabla usuarios no encontrada',
        details: tablaVerificacion
      }
    }

    console.log('✅ Supabase inicializado correctamente')
    return {
      success: true,
      message: 'Supabase configurado y listo para usar',
      details: {
        url: supabaseUrl,
        tabla: 'usuarios',
        timestamp: new Date().toISOString()
      }
    }

  } catch (error) {
    console.error('❌ Error en inicialización:', error)
    return {
      success: false,
      step: 'initialization',
      message: 'Error durante la inicialización',
      error: error.message
    }
  }
}

// Log de inicialización (solo en desarrollo)
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 Supabase Client configurado:')
  console.log('📍 URL:', supabaseUrl ? '✅ Configurada' : '❌ Faltante')
  console.log('🔑 Key:', supabaseKey ? '✅ Configurada' : '❌ Faltante')
  
  // Verificar tabla automáticamente en desarrollo
  verificarYCrearTabla()
}

/**
 * Función para formatear fechas desde Supabase con zona horaria correcta
 * @param {string} dateString - Fecha en formato ISO string
 * @returns {string} - Fecha formateada en zona horaria de Perú
 */
export const formatDateFromSupabase = (dateString) => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    
    return date.toLocaleDateString('es-PE', {
      timeZone: 'America/Lima',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2}):(\d{2})/, '$1-$2-$3 $4:$5:$6');
  } catch (error) {
    console.error('Error formateando fecha:', error);
    return '-';
  }
};